import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { cx } from '../internal/utils.js';
import { useNkStrings } from '../i18n/index.js';
import { Icon } from '../internal/icons.js';
const ToastContext = createContext(null);
const TONE_ICON = {
    info: 'info',
    success: 'check-circle',
    warning: 'warning',
    danger: 'alert-circle',
};
export function ToastProvider({ children, durationMs = 5000, maxToasts = 4, className, }) {
    const { t } = useNkStrings();
    const [toasts, setToasts] = useState([]);
    const nextId = useRef(0);
    const timers = useRef(new Map());
    const dismiss = useCallback((id) => {
        const timer = timers.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
        setToasts((current) => current.filter((item) => item.id !== id));
    }, []);
    const toast = useCallback((input) => {
        nextId.current += 1;
        const id = `nk-toast-${nextId.current}`;
        const value = typeof input === 'string' ? { message: input } : input;
        const item = {
            ...value,
            id,
            tone: value.tone ?? 'info',
            durationMs: value.durationMs ?? durationMs,
        };
        setToasts((current) => [...current.slice(-(maxToasts - 1)), item]);
        if (item.durationMs > 0) {
            const timer = setTimeout(() => dismiss(id), item.durationMs);
            timers.current.set(id, timer);
        }
        return id;
    }, [dismiss, durationMs, maxToasts]);
    useEffect(() => () => {
        for (const timer of timers.current.values())
            clearTimeout(timer);
        timers.current.clear();
    }, []);
    const api = useMemo(() => ({ toast, dismiss, toasts }), [dismiss, toast, toasts]);
    return (_jsxs(ToastContext.Provider, { value: api, children: [children, _jsx("div", { className: cx('nk-toast-region', className), "aria-live": "polite", "aria-label": t('toast.region'), children: toasts.map((item) => (_jsx(Toast, { item: item, onDismiss: dismiss }, item.id))) })] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (!context)
        throw new Error('useToast must be used inside <ToastProvider>');
    return context;
}
export function Toast({ item, onDismiss }) {
    const { t } = useNkStrings();
    return (_jsxs("div", { className: cx('nk-toast', `nk-toast--${item.tone}`), "data-toast-id": item.id, children: [_jsx("span", { className: "nk-toast__icon", "aria-hidden": "true", children: _jsx(Icon, { name: TONE_ICON[item.tone], size: 18 }) }), _jsxs("div", { className: "nk-toast__body", children: [item.title ? _jsx("p", { className: "nk-toast__title", children: item.title }) : null, _jsx("p", { className: "nk-toast__message", children: item.message }), item.action ? (_jsx("button", { type: "button", className: "nk-toast__action", onClick: item.action.onClick, children: item.action.label })) : null] }), _jsx("button", { type: "button", className: "nk-toast__close", "aria-label": t('toast.dismiss'), onClick: () => onDismiss(item.id), children: _jsx(Icon, { name: "close", size: 16 }) })] }));
}
//# sourceMappingURL=Toast.js.map